import { Link } from '@tanstack/react-router'
import { onHandQty } from '../lib/stock'
import type { ColumnDef } from '@tanstack/react-table'
import type { InventoryViewItem } from '@/api/endpoints/inventory-view'
import { Badge } from '@/components/ui/badge'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'
import { noStockReason } from '@/shared/lib/stock-kind'

const formatTimestamp = (value?: string | null) => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * A dish, a variant or a not-tracked article (CHG-2026-09-stock-usage-and-price-twins, task 11.1):
 * the row holds no stock of its own, so its stock columns read "–" and "Verkäuflich" shows the
 * online number derived from the tracked parts. A plain article's row is exactly what it was.
 */
export function isDerivedRow(row: InventoryViewItem): boolean {
  return noStockReason(row) !== null
}

/** A stock column's cell: the quantity as before, or a muted dash on a derived row. */
function stockCell(row: InventoryViewItem, value: number, className: string) {
  if (isDerivedRow(row)) {
    return <span className="font-mono text-muted-foreground">–</span>
  }
  return <span className={className}>{formatStockDisplay(value)}</span>
}

function DerivedSellable({ row, t }: { row: InventoryViewItem; t: Translate }) {
  if (row.unlimited || row.onlineQty == null) {
    return (
      <Badge variant="outline" className="font-normal">
        {row.stockKind === 'not_tracked'
          ? t('derived.notTracked')
          : t('derived.unlimited')}
      </Badge>
    )
  }
  return (
    <div className="flex flex-col" title={t('derived.onlineHint')}>
      <span className="font-mono font-medium">
        {formatStockDisplay(row.onlineQty)}
      </span>
      {row.limitingName || row.limitingSku ? (
        <span className="text-xs text-muted-foreground">
          {t('derived.limitedBy', {
            name: row.limitingName ?? row.limitingSku,
          })}
        </span>
      ) : null}
    </div>
  )
}

export function buildInventoryColumns(
  t: Translate,
  tCommon: (key: string) => string,
): Array<ColumnDef<InventoryViewItem>> {
  return [
    {
      accessorKey: 'sku',
      header: tCommon('sku'),
      size: 140,
      cell: ({ row }) => (
        <Link
          to="/products/$sku"
          params={{ sku: row.original.sku }}
          className="font-mono text-primary hover:underline"
        >
          {row.original.sku}
        </Link>
      ),
      meta: { label: tCommon('sku') },
    },
    {
      accessorKey: 'name',
      header: tCommon('name'),
      size: 260,
      cell: ({ row }) => {
        const kind = noStockReason(row.original)
        if (!kind) return row.original.name
        return (
          <span className="flex flex-wrap items-center gap-2">
            <span>{row.original.name}</span>
            <Badge variant="secondary">{t(`kind.${kind}`)}</Badge>
          </span>
        )
      },
      meta: { label: tCommon('name') },
    },
    // Operator-facing order: what is on the shelf, what is promised, what is held
    // back, and what the shop can sell. "Available" is the stored intermediate
    // (on-hand − reserved) and is not shown as its own column.
    {
      id: 'onHand',
      header: t('table.onHand'),
      size: 100,
      accessorFn: (row) => onHandQty(row.availableQty, row.reservedQty),
      cell: ({ row, getValue }) =>
        stockCell(row.original, getValue<number>(), 'font-mono font-medium'),
      meta: { label: t('table.onHand') },
    },
    {
      accessorKey: 'reservedQty',
      header: t('table.reserved'),
      size: 100,
      cell: ({ row }) =>
        stockCell(row.original, row.original.reservedQty, 'font-mono'),
      meta: { label: t('table.reserved') },
    },
    {
      accessorKey: 'safetyBufferQty',
      header: t('table.safetyBuffer'),
      size: 120,
      cell: ({ row }) =>
        stockCell(row.original, row.original.safetyBufferQty ?? 0, 'font-mono'),
      meta: { label: t('table.safetyBuffer') },
    },
    {
      id: 'incoming',
      header: t('table.incoming'),
      size: 110,
      // Sortable server-side (`sortBy=incoming`); muted at 0 so a real announcement stands out.
      accessorFn: (row) => row.incomingQty ?? 0,
      cell: ({ row, getValue }) => {
        const qty = getValue<number>()
        return stockCell(
          row.original,
          qty,
          qty > 0 ? 'font-mono' : 'font-mono text-muted-foreground',
        )
      },
      meta: { label: t('table.incoming') },
    },
    {
      // The base unit as shown (CHG-2026-09-stock-usage-and-price-twins, D9): every quantity in
      // the row is in it.
      id: 'unit',
      header: t('table.unit'),
      size: 90,
      accessorFn: (row) => row.stockUnit ?? '',
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.stockUnit ?? '–'}
        </span>
      ),
      meta: { label: t('table.unit') },
    },
    {
      id: 'sellable',
      header: t('table.sellable'),
      size: 150,
      // Server-computed (inventory_sellable view) — it also covers SKUs without an inventory
      // row, which a local re-derivation would get wrong. Absent only on an older backend.
      accessorFn: (row) => row.sellableQty ?? 0,
      cell: ({ row, getValue }) =>
        isDerivedRow(row.original) ? (
          <DerivedSellable row={row.original} t={t} />
        ) : (
          <span className="font-mono font-medium">
            {formatStockDisplay(getValue<number>())}
          </span>
        ),
      meta: { label: t('table.sellable') },
    },
    {
      accessorKey: 'updatedAt',
      header: t('table.updated'),
      size: 170,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatTimestamp(row.original.updatedAt)}
        </span>
      ),
      meta: { label: t('table.updated') },
    },
  ]
}
